import { Plus, X } from 'lucide-react';

// Repeatable copy input group (C7-extension) for headlines / primary texts /
// descriptions. Renders 1..max inputs with a live char count + remove button per
// row and an "+ Add" button. Remove is disabled at `min` items; Add at `max`.
// Single mode (max === 1, the Dynamic Creative toggle off): the Add and remove
// buttons are hidden entirely — it reads as one plain field.
// Pure presentation — the parent owns the string[] and validation.
export function RepeatableCopyField({
  label,
  optional,
  values,
  onChange,
  soft,
  min,
  max,
  addLabel,
  multiline,
  helper,
}: {
  label: string;
  optional?: boolean;
  values: string[];
  onChange: (next: string[]) => void;
  soft: number;
  min: number;
  max: number;
  addLabel: string;
  multiline?: boolean;
  helper?: string;
}) {
  const setAt = (i: number, v: string) => onChange(values.map((x, idx) => (idx === i ? v : x)));
  const removeAt = (i: number) => onChange(values.filter((_, idx) => idx !== i));
  const add = () => onChange([...values, '']);
  const singleMode = max === 1;

  return (
    <div className="max-w-xl space-y-2">
      <label className="block text-sm font-medium text-slate-700">
        {label}
        {optional && <span className="ml-1 font-normal text-slate-400">(optional)</span>}
      </label>
      {helper && <p className="text-xs text-slate-400">{helper}</p>}

      {values.map((value, i) => {
        const over = value.length > soft;
        return (
          <div key={i} className="flex items-start gap-2">
            <div className="flex-1">
              {multiline ? (
                <textarea
                  rows={3}
                  value={value}
                  aria-label={`${label} ${i + 1}`}
                  onChange={(e) => setAt(i, e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
                />
              ) : (
                <input
                  type="text"
                  value={value}
                  aria-label={`${label} ${i + 1}`}
                  onChange={(e) => setAt(i, e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
                />
              )}
              <div className="mt-0.5 text-right">
                <span className={['text-xs', over ? 'text-amber-600' : 'text-slate-400'].join(' ')}>
                  {value.length}/{soft}
                </span>
              </div>
            </div>
            {!singleMode && (
              <button
                type="button"
                aria-label={`Remove ${label} ${i + 1}`}
                onClick={() => removeAt(i)}
                disabled={values.length <= min}
                className="mt-2 rounded p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        );
      })}

      {!singleMode && (
        <button
          type="button"
          onClick={add}
          disabled={values.length >= max}
          className="inline-flex items-center gap-1 text-xs font-medium text-kyck-700 hover:underline disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" /> {addLabel}
        </button>
      )}
    </div>
  );
}
